import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type React from 'react';
import { useState } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

import FilterDropdown from './FilterDropdown';
import type { DateRangeValue, FilterFieldConfig, FilterOption, MasterFilterValues } from './types';

interface AdvancedFilterModalProps {
  open: boolean;
  onClose: () => void;
  fields: FilterFieldConfig[];
  values: MasterFilterValues;
  onApply: (key: string, value: unknown) => void;
  dynamicOptions?: Record<string, FilterOption[]>;
}

const FIELD_LABEL_SX = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: `${NAVY}99`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  mb: 1.5,
};

const DATE_FIELD_SX = {
  width: { xs: '100%', sm: 160 },
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: RADIUS_ITEM,
    backgroundColor: SURFACE_CONTAINER_LOWEST,
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(21, 24, 58, 0.12)',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Jost, sans-serif',
    transform: 'translate(14px, 9px)',
    '&.Mui-focused, &.MuiFormLabel-filled': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
} as const;

const TEXT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    backgroundColor: SURFACE_CONTAINER_LOWEST,
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Jost, sans-serif',
  },
} as const;

const SELECT_FIELD_SX = {
  minWidth: { xs: 0, sm: 200 },
  borderRadius: RADIUS_ITEM,
  backgroundColor: SURFACE_CONTAINER_LOWEST,
  fontFamily: 'Jost, sans-serif',
  fontSize: '0.875rem',
} as const;

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={FIELD_LABEL_SX}>{children}</Typography>
);

const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({
  open,
  onClose,
  fields,
  values,
  onApply,
  dynamicOptions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Local draft state — only committed on Apply
  const [draft, setDraft] = useState<MasterFilterValues>(() => {
    const init: MasterFilterValues = {};
    for (const f of fields) init[f.key] = values[f.key];
    return init;
  });

  const handleOpen = () => {
    // Sync draft with current values each time modal opens
    const init: MasterFilterValues = {};
    for (const f of fields) init[f.key] = values[f.key];
    setDraft(init);
  };

  const set = (key: string, value: unknown) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    for (const f of fields) onApply(f.key, draft[f.key]);
    onClose();
  };

  const handleClear = () => {
    const cleared: MasterFilterValues = {};
    for (const f of fields) cleared[f.key] = f.defaultValue ?? undefined;
    setDraft(cleared);
  };

  const renderField = (field: FilterFieldConfig) => {
    // Respect visibleWhen predicates using current draft values
    if (field.visibleWhen && !field.visibleWhen(draft)) return null;

    const val = draft[field.key];

    switch (field.kind) {
      case 'dateRange': {
        const dr = (val as DateRangeValue | undefined) ?? { start: null, end: null };
        return (
          <Box key={field.key}>
            <FieldLabel>{field.label}</FieldLabel>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <DatePicker
                  label="Desde"
                  value={dr.start}
                  onChange={(v) => set(field.key, { ...dr, start: v })}
                  maxDate={dr.end ?? undefined}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', sx: DATE_FIELD_SX } }}
                />
                <Typography
                  sx={{
                    color: `${NAVY}60`,
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.85rem',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  —
                </Typography>
                <DatePicker
                  label="Hasta"
                  value={dr.end}
                  onChange={(v) => set(field.key, { ...dr, end: v })}
                  minDate={dr.start ?? undefined}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', sx: DATE_FIELD_SX } }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        );
      }

      case 'text': {
        return (
          <Box key={field.key}>
            <FieldLabel>{field.label}</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder={`Buscar por ${field.label.toLowerCase()}…`}
              value={(val as string) ?? ''}
              onChange={(e) => set(field.key, e.target.value)}
              sx={TEXT_FIELD_SX}
            />
          </Box>
        );
      }

      case 'multiSelect': {
        const options = field.options ?? dynamicOptions?.[field.key] ?? [];
        const selected = (val as string[] | undefined) ?? [];
        return (
          <Box key={field.key}>
            <FieldLabel>{field.label}</FieldLabel>
            <FilterDropdown options={options} selectedValues={selected} onChange={(v) => set(field.key, v)} />
          </Box>
        );
      }

      case 'singleSelect':
      case 'userSelect': {
        const options =
          field.kind === 'userSelect'
            ? (dynamicOptions?.[field.key] ?? field.options ?? [])
            : (field.options ?? dynamicOptions?.[field.key] ?? []);
        return (
          <Box key={field.key}>
            <FieldLabel>{field.label}</FieldLabel>
            <Select
              size="small"
              displayEmpty
              fullWidth
              value={(val as string) ?? ''}
              onChange={(e) => set(field.key, e.target.value || null)}
              sx={SELECT_FIELD_SX}
            >
              <MenuItem value="">
                <Typography sx={{ fontWeight: 400, fontSize: '0.875rem', color: 'text.secondary' }}>Todos</Typography>
              </MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{opt.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        transition: { onEnter: handleOpen },
        paper: {
          sx: {
            borderRadius: isMobile ? 0 : RADIUS_CARD,
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: isMobile ? 'none' : SHADOW,
          },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pt: { xs: 2, sm: 3 },
          pb: 1.5,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: NAVY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TuneIcon sx={{ color: YELLOW, fontSize: '1.1rem' }} />
        </Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: NAVY, flex: 1 }}>
          Filtros avanzados
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: `${NAVY}80` }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ opacity: 0.12 }} />

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{fields.map(renderField)}</Box>
      </DialogContent>

      <Divider sx={{ opacity: 0.12 }} />

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        {!isMobile && (
          <Button
            onClick={handleClear}
            sx={{
              textTransform: 'none',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 500,
              color: `${NAVY}80`,
              mr: 'auto',
            }}
          >
            Limpiar
          </Button>
        )}
        <Button
          onClick={handleApply}
          variant="contained"
          fullWidth={isMobile}
          sx={{
            textTransform: 'none',
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            backgroundColor: NAVY,
            color: '#fff',
            px: 3,
            height: { xs: 44, sm: 'auto' },
            '&:hover': { backgroundColor: `${NAVY}dd` },
          }}
        >
          Aplicar filtros
        </Button>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {isMobile && (
            <Button
              onClick={handleClear}
              fullWidth
              sx={{
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 500,
                color: `${NAVY}80`,
                height: 40,
              }}
            >
              Limpiar
            </Button>
          )}
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              textTransform: 'none',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_COIN,
              color: NAVY,
              borderColor: `${NAVY}30`,
              px: 2.5,
              height: { xs: 40, sm: 'auto' },
            }}
          >
            Cancelar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedFilterModal;
